import type {
  ViewModeLayouts,
  DashboardLayouts,
  NamedLayout,
  Widget,
} from "./dashboardLayoutTypes";
import { API_ENDPOINTS, LOG_MESSAGES } from "./dashboardLayoutConstants";
import {
  convertWidgetsToLayouts,
  convertLayoutsToWidgets,
  createGridConfig,
} from "./dashboardLayoutTransformers";
import type { GridConfig } from "./dashboardLayoutTypes";
import { authService } from "@/features/auth/services/authService";
import { log } from "@/shared/utils/logger";
import { getDashboardStorageScope } from '../dashboardStorageScope';

/** Shape of a layout record as returned by the backend API */
interface ApiLayoutRecord {
  viewMode?: string;
  layoutName?: string | null;
  layout_name?: string | null;
  isDefault?: boolean;
  widgets: Widget[];
  gridConfig?: GridConfig;
  updatedAt?: string;
  createdAt?: string;
  id?: string;
}

type CreatedApiLayout = ApiLayoutRecord & { id: string; layoutName: string };

const hasNamedLayoutId = (layout: ApiLayoutRecord): layout is ApiLayoutRecord & { id: string } =>
  typeof layout.id === 'string' && layout.id.length > 0;

/* ============================================================================
 * DASHBOARD LAYOUT API OPERATIONS
 * Handles all API communication for dashboard layouts
 * ============================================================================ */

/**
 * Fetch layouts from server
 */
export async function fetchLayouts(): Promise<{
  layouts: ViewModeLayouts;
  lastModified: { [viewMode: string]: string };
  widgetData?: { [viewMode: string]: Widget[] };
}> {
  await authService.ensureValidToken();
  const response = await authService.authenticatedFetch(API_ENDPOINTS.LAYOUTS, {
    method: "GET",
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error("Authentication required");
    }
    if (response.status === 404) {
      log.debug('DashboardLayoutAPI', LOG_MESSAGES.BACKEND_FALLBACK);
      return { layouts: {}, lastModified: {} };
    }
    throw new Error(`Failed to fetch layouts: ${response.statusText}`);
  }

  const data = await response.json();

  // Convert backend response to frontend format
  const layouts: ViewModeLayouts = {};
  const lastModified: { [viewMode: string]: string } = {};
  const widgetData: { [viewMode: string]: Widget[] } = {};

  // Handle single layout response or multiple layouts
  if (data.widgets) {
    // Single layout response
    layouts["default"] = convertWidgetsToLayouts(
      data.widgets,
      data.gridConfig
    );
    lastModified["default"] =
      data.updatedAt || new Date().toISOString();
    widgetData["default"] = data.widgets;
  } else if (Array.isArray(data)) {
    // Multiple layouts response
    const layoutsByViewMode = (data as ApiLayoutRecord[]).reduce((acc: Record<string, ApiLayoutRecord[]>, layout: ApiLayoutRecord) => {
      const viewMode = layout.viewMode || "default";
      if (!acc[viewMode]) {
        acc[viewMode] = [];
      }
      acc[viewMode].push(layout);
      return acc;
    }, {});

    Object.entries(layoutsByViewMode).forEach(([viewMode, viewModeLayouts]) => {
      const currentWorkingLayout = viewModeLayouts.find((layout: ApiLayoutRecord) =>
        layout.layoutName == null && layout.layout_name == null
      );
      const defaultNamedLayout = viewModeLayouts.find((layout: ApiLayoutRecord) =>
        layout.isDefault && (layout.layoutName != null || layout.layout_name != null)
      );
      const selectedLayout = currentWorkingLayout || defaultNamedLayout;

      if (!selectedLayout) {
        return;
      }

      layouts[viewMode] = convertWidgetsToLayouts(
        selectedLayout.widgets,
        selectedLayout.gridConfig
      );
      lastModified[viewMode] =
        selectedLayout.updatedAt || new Date().toISOString();
      widgetData[viewMode] = selectedLayout.widgets;
    });
  }

  return { layouts, lastModified, widgetData };
}

/**
 * Save layout to server
 */
export async function saveLayoutToServer(
  viewMode: string,
  layouts: DashboardLayouts,
  widgetData?: Widget[]
): Promise<{ success: boolean; lastModified: string }> {
  await authService.ensureValidToken();

  // Minimal payload guard to prevent 422s when data not ready
  const widgetsPayload = convertLayoutsToWidgets(layouts, widgetData) || [];
  const gridConfigPayload = createGridConfig(layouts) || { breakpoints: {} };

  // Ensure required shapes
  if (!Array.isArray(widgetsPayload)) {
    throw new Error("Invalid dashboard payload: widgets is not an array");
  }
  if (!gridConfigPayload || typeof gridConfigPayload !== "object") {
    throw new Error("Invalid dashboard payload: grid_config missing");
  }

  const response = await authService.authenticatedFetch(
    API_ENDPOINTS.LAYOUT_BY_MODE(viewMode),
    {
      method: "PUT",
      body: JSON.stringify({
        widgets: widgetsPayload,
        grid_config: gridConfigPayload,
        is_default: false,
      }),
    }
  );

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    if (response.status === 401) {
      throw new Error("Authentication required");
    }
    if (response.status === 403) {
      throw new Error("Access denied to this view mode");
    }
    throw new Error(`Failed to save layout: ${response.status} ${response.statusText} ${text}`);
  }

  const data = await response.json();
  return {
    success: true,
    lastModified: data.updatedAt || new Date().toISOString(),
  };
}

/**
 * Reset layout on server
 */
export async function resetLayoutOnServer(viewMode: string): Promise<boolean> {
  await authService.ensureValidToken();
  const response = await authService.authenticatedFetch(
    API_ENDPOINTS.LAYOUT_BY_MODE(viewMode),
    {
      method: "DELETE",
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to reset layout: ${response.statusText}`);
  }

  return true;
}

/**
 * Fetch named layouts from server
 */
export async function fetchNamedLayouts(
  viewMode: string
): Promise<NamedLayout[]> {
  await authService.ensureValidToken();
  const response = await authService.authenticatedFetch(
    API_ENDPOINTS.NAMED_LAYOUTS(viewMode),
    {
      method: "GET",
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch named layouts: ${response.statusText}`);
  }

  const layouts = await response.json();
  // log.debug('DashboardLayoutAPI', LOG_MESSAGES.NAMED_LAYOUTS_FETCH, layouts);

  // Convert format
  const namedLayouts: NamedLayout[] = (
    Array.isArray(layouts) ? layouts : []
  ).filter(hasNamedLayoutId).map((layout) => {
    // log.debug('DashboardLayoutAPI', `📊 Processing layout ${layout.id}:`, {
    //   name: layout.layoutName,
    //   hasGridConfig: !!layout.gridConfig,
    //   hasBreakpoints: !!layout.gridConfig?.breakpoints,
    //   widgetCount: layout.widgets?.length
    // });

    return {
      id: layout.id,
      name: layout.layoutName || layout.layout_name || '',
      layouts: convertWidgetsToLayouts(layout.widgets, layout.gridConfig),
      widgets: layout.widgets, // Preserve widget data including visibility states
      isDefault: layout.isDefault || false,
      createdAt: layout.createdAt,
      lastModified: layout.updatedAt,
    };
  });

  return namedLayouts;
}

/**
 * Save named layout to server
 */
export async function saveNamedLayoutToServer(
  viewMode: string,
  name: string,
  layouts: DashboardLayouts,
  widgetData?: Widget[]
): Promise<NamedLayout> {
  const owner = getDashboardStorageScope();
  const sessionIdentity = authService.getSessionIdentity();
  const assertOwner = () => {
    if (getDashboardStorageScope() !== owner || authService.getSessionIdentity() !== sessionIdentity) {
      throw new Error('Authentication context changed. Retry this action.');
    }
  };
  const payload = {
    name,
    widgets: convertLayoutsToWidgets(layouts, widgetData),
    grid_config: createGridConfig(layouts),
  };

  try {
    await authService.ensureValidToken();
    assertOwner();
    // A refresh retry must carry the same key. Recover only this creation's
    // original acknowledgement; never adopt an existing layout by its name.
    const requestId = Array.from(crypto.getRandomValues(new Uint8Array(16)),
      byte => byte.toString(16).padStart(2, '0')).join('');
    const isCreatedLayout = (value: unknown): value is CreatedApiLayout => {
      if (!value || typeof value !== 'object') return false;
      const row = value as ApiLayoutRecord;
      return typeof row.id === 'string' && row.id.length > 0
        && row.layoutName === name && row.viewMode === viewMode
        && Array.isArray(row.widgets) && !!row.gridConfig
        && typeof row.gridConfig === 'object' && !Array.isArray(row.gridConfig);
    };
    const recover = async (): Promise<CreatedApiLayout> => {
      assertOwner();
      const response = await authService.authenticatedFetch(
        `${API_ENDPOINTS.NAMED_LAYOUTS(viewMode)}/creation-receipts/${requestId}`,
        { method: 'GET' },
      );
      assertOwner();
      if (!response.ok) throw new Error('Could not confirm the layout save. Check saved layouts before trying again.');
      const receipt = await response.json();
      assertOwner();
      if (receipt?.requestId !== requestId || !isCreatedLayout(receipt?.layout)) {
        throw new Error('Could not confirm the layout save. Check saved layouts before trying again.');
      }
      return receipt.layout;
    };
    let response: Response | undefined;
    let layout: CreatedApiLayout | undefined;
    try {
      response = await authService.authenticatedFetch(
        API_ENDPOINTS.NAMED_LAYOUTS(viewMode),
        { method: 'POST', body: JSON.stringify({ ...payload, creation_request_id: requestId }) },
      );
    } catch {
      layout = await recover();
    }
    assertOwner();

    if (response && !response.ok && (response.status >= 500 || response.status === 409)) {
      layout = await recover();
    } else if (response && !response.ok) {
      const errorText = await response.text();
      log.error("DashboardLayoutAPI", "Save named layout error:", {
        status: response.status,
        statusText: response.statusText,
        error: errorText,
      });

      // Provide user-friendly error messages
      if (response.status === 401) {
        throw new Error("Your session has expired. Please log in again.");
      } else if (response.status === 403) {
        throw new Error("You do not have permission to save layouts.");
      } else if (response.status === 400) {
        throw new Error(`Invalid layout data: ${errorText}`);
      } else {
        throw new Error(
          `Failed to save layout. Please try again. (${response.status})`
        );
      }
    }

    if (response?.ok) {
      try {
        const value: unknown = await response.json();
        assertOwner();
        if (!isCreatedLayout(value)) throw new Error('Incomplete layout creation acknowledgement');
        layout = value;
      } catch {
        layout = await recover();
      }
    }
    if (!layout) throw new Error('Could not confirm the layout save. Check saved layouts before trying again.');
    log.info(
      "DashboardLayoutAPI",
      `Layout saved successfully to database: ${layout.id}`
    );

    // Convert backend format to frontend format
    return {
      id: layout.id,
      name: layout.layoutName, // Backend returns camelCase
      layouts: convertWidgetsToLayouts(
        layout.widgets,
        layout.gridConfig
      ),
      widgets: layout.widgets, // Preserve widget data including visibility states
      isDefault: layout.isDefault || false,
      createdAt: layout.createdAt,
      lastModified: layout.updatedAt,
    };
  } catch (error) {
    // Log the error but re-throw it to prevent silent failures
    log.error("DashboardLayoutAPI", "Failed to save named layout:", error);
    throw error; // Re-throw to ensure error bubbles up to UI
  }
}

/**
 * Update named layout on server
 */
export async function updateNamedLayoutToServer(
  viewMode: string,
  layoutId: string,
  name: string,
  layouts: DashboardLayouts,
  widgetData?: Widget[],
  isDefault = false
): Promise<NamedLayout> {
  const payload = {
    layout_name: name,
    widgets: convertLayoutsToWidgets(layouts, widgetData),
    grid_config: createGridConfig(layouts),
    is_default: isDefault,
  };

  try {
    await authService.ensureValidToken();
    const response = await authService.authenticatedFetch(
      API_ENDPOINTS.NAMED_LAYOUT_BY_ID(viewMode, layoutId),
      {
        method: "PUT",
        body: JSON.stringify(payload),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      log.error("DashboardLayoutAPI", "Update named layout error:", {
        status: response.status,
        statusText: response.statusText,
        error: errorText,
      });

      if (response.status === 401) {
        throw new Error("Your session has expired. Please log in again.");
      } else if (response.status === 403) {
        throw new Error("You do not have permission to update layouts.");
      } else if (response.status === 404) {
        throw new Error("The saved layout no longer exists.");
      } else if (response.status === 400) {
        throw new Error(`Invalid layout data: ${errorText}`);
      } else {
        throw new Error(
          `Failed to update layout. Please try again. (${response.status})`
        );
      }
    }

    const layout = await response.json();
    return {
      id: layout.id,
      name: layout.layoutName,
      layouts: convertWidgetsToLayouts(
        layout.widgets,
        layout.gridConfig
      ),
      widgets: layout.widgets,
      isDefault: layout.isDefault || false,
      createdAt: layout.createdAt,
      lastModified: layout.updatedAt,
    };
  } catch (error) {
    log.error("DashboardLayoutAPI", "Failed to update named layout:", error);
    throw error;
  }
}

/**
 * Load named layout from server
 */
export async function loadNamedLayoutFromServer(
  viewMode: string,
  layoutId: string
): Promise<DashboardLayouts> {
  await authService.ensureValidToken();
  const response = await authService.authenticatedFetch(
    API_ENDPOINTS.NAMED_LAYOUT_BY_ID(viewMode, layoutId),
    {
      method: "GET",
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to load named layout: ${response.statusText}`);
  }

  const layout = await response.json();
  // log.debug('DashboardLayoutAPI', '📊 Full backend response:', JSON.stringify(layout, null, 2));
  // log.debug('DashboardLayoutAPI', '📊 gridConfig from backend:', layout.gridConfig);
  // log.debug('DashboardLayoutAPI', '📊 gridConfig type:', typeof layout.gridConfig);
  // log.debug('DashboardLayoutAPI', '📊 gridConfig.breakpoints:', layout.gridConfig?.breakpoints);
  // log.debug('DashboardLayoutAPI', '📊 widgets from backend:', layout.widgets);

  // If gridConfig exists and has breakpoints, use it directly
  if (layout.gridConfig?.breakpoints) {
    // log.debug('DashboardLayoutAPI', '✅ Using gridConfig.breakpoints directly');
    // Make sure we return a proper DashboardLayouts object
    const breakpoints = layout.gridConfig.breakpoints;
    return {
      lg: breakpoints.lg || [],
      md: breakpoints.md || [],
      sm: breakpoints.sm || [],
      xs: breakpoints.xs || [],
      xxs: breakpoints.xxs || [],
    };
  }

  // Otherwise convert widgets to layouts
  // log.debug('DashboardLayoutAPI', '⚠️ No breakpoints in gridConfig, converting from widgets');
  return convertWidgetsToLayouts(
    layout.widgets,
    layout.gridConfig
  );
}

/**
 * Delete named layout on server
 */
export async function deleteNamedLayoutOnServer(
  viewMode: string,
  layoutId: string
): Promise<boolean> {
  await authService.ensureValidToken();
  const response = await authService.authenticatedFetch(
    API_ENDPOINTS.NAMED_LAYOUT_BY_ID(viewMode, layoutId),
    {
      method: "DELETE",
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to delete named layout: ${response.statusText}`);
  }

  return true;
}

/**
 * Set default layout on server
 */
export async function setDefaultLayoutOnServer(
  viewMode: string,
  layoutId: string
): Promise<boolean> {
  await authService.ensureValidToken();
  const response = await authService.authenticatedFetch(
    API_ENDPOINTS.SET_DEFAULT(viewMode, layoutId),
    {
      method: "POST", // Changed from PUT to match backend expectation
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to set default layout: ${response.statusText}`);
  }

  return true;
}
